import { C, cardAim, cardNeedsTarget, roleStats } from "./accessors";
import { applyAoeHit } from "./combat";
import { canAfford, spendMana } from "./mana";
import { cycleAfterPlay, spawnUnit, type SpawnAnchor } from "./state";
import {
  castBoardHalf,
  castCone,
  castCurseCluster,
  castDelayedBlast,
  castSacrificeNuke,
  castShockwave,
  castZone,
} from "./spells";
import type { InfiniteWarState, IwCardId, IwRoleId, IwTeam, IwUnit } from "./types";
import { killUnit } from "./veil";

type IwDeployDef = {
  kind: string;
  damage?: number;
  heal?: number;
  healPerUnit?: number;
  buffMul?: number;
  blightSec?: number;
  radius?: number;
  radiusBonusPerUnit?: number;
};

export function playCard(
  state: InfiniteWarState,
  team: IwTeam,
  card: IwCardId,
  targetX?: number,
  targetY?: number,
): boolean {
  const def = C.cards[card];
  const cards = state.cards[team];
  if (def.team !== team || !cards.hand.includes(card) || !canAfford(state, team, def.cost)) {
    return false;
  }
  const aim = cardAim(card);
  if (cardNeedsTarget(aim) && (targetX == null || targetY == null)) {
    return false;
  }
  let ok = false;
  if (def.kind === "summon" && "roleId" in def && def.roleId) {
    const count = "count" in def && typeof def.count === "number" ? def.count : 1;
    const formation =
      "formation" in def && typeof def.formation === "string" ? def.formation : "single";
    const firstId = state.nextId;
    ok = trySummonPack(
      state,
      team,
      def.roleId as IwRoleId,
      def.cost,
      count,
      formation,
      { x: targetX!, y: targetY! },
    );
    if (ok && "deploy" in def && def.deploy) {
      const endId = state.nextId;
      const spawned = state.units.filter(
        (u) => u.id >= firstId && u.id < endId && u.phase === "alive",
      );
      applySummonDeploy(state, team, card, def.deploy as IwDeployDef, spawned);
    }
  } else if (def.kind === "shockwave") {
    ok = castShockwave(state, team, targetX!, targetY!, def.cost);
  } else if (def.kind === "cone") {
    ok = castCone(state, team, targetX!, targetY!, def.cost);
  } else if (def.kind === "zone") {
    ok = castZone(state, team, targetX!, targetY!, def.cost);
  } else if (def.kind === "delayed_blast") {
    ok = castDelayedBlast(state, team, targetX!, targetY!, def.cost);
  } else if (def.kind === "curse_cluster") {
    ok = castCurseCluster(state, team, targetX!, targetY!, def.cost);
  } else if (def.kind === "sacrifice_nuke") {
    ok = castSacrificeNuke(state, team, targetX!, targetY!, def.cost);
  } else if (def.kind === "board_half") {
    ok = castBoardHalf(state, team, def.cost);
  }
  if (!ok) return false;
  cycleAfterPlay(cards, card);
  return true;
}

export function trySummon(
  state: InfiniteWarState,
  team: IwTeam,
  roleId: IwRoleId,
  cost: number,
  anchor?: SpawnAnchor,
): boolean {
  return trySummonPack(state, team, roleId, cost, 1, "single", anchor);
}

export function trySummonPack(
  state: InfiniteWarState,
  team: IwTeam,
  roleId: IwRoleId,
  cost: number,
  count: number,
  formation: string,
  anchor?: SpawnAnchor,
): boolean {
  if (!canAfford(state, team, cost)) return false;
  const stats = roleStats(roleId);
  if (stats.tags.includes("demigod")) return false;
  if (!spendMana(state, team, cost)) return false;
  const offsets = formationOffsets(formation, count);
  const baseSeed = state.nextId;
  for (let i = 0; i < count; i += 1) {
    const off = offsets[i] ?? { dx: 0, dy: 0 };
    spawnUnit(state, team, roleId, baseSeed + i, off.dx, off.dy, anchor);
  }
  return true;
}

/** Resolve pack/elite on-deploy fanfare from catalog `deploy` SSOT. */
export function applySummonDeploy(
  state: InfiniteWarState,
  team: IwTeam,
  card: IwCardId,
  deploy: IwDeployDef,
  spawned: readonly IwUnit[],
): void {
  if (spawned.length === 0) return;
  const centroid = packCentroid(spawned);
  const count = spawned.length;
  const radius =
    (deploy.radius ?? 64) + (deploy.radiusBonusPerUnit ?? 0) * Math.max(0, count - 1);

  state.events.push({
    kind: "deploy_effect",
    team,
    card,
    effect: deploy.kind,
    x: centroid.x,
    y: centroid.y,
    radius,
  });

  if (deploy.kind === "opening_volley") {
    const dmg = deploy.damage ?? 4;
    const shotR = deploy.radius ?? 96;
    for (const shooter of spawned) {
      const target = nearestEnemyInRadius(state, shooter, shotR);
      if (!target) continue;
      applyDirectHit(state, target, dmg, team);
      state.events.push({
        kind: "attack",
        team,
        unitId: shooter.id,
        roleId: shooter.roleId,
        x: shooter.x,
        y: shooter.y,
        targetX: target.x,
        targetY: target.y,
      });
    }
    return;
  }

  if (deploy.kind === "shield_pulse") {
    const heal = (deploy.healPerUnit ?? deploy.heal ?? 6) * count;
    const buff = deploy.buffMul ?? 1.3;
    for (const ally of state.units) {
      if (ally.phase !== "alive" || ally.team !== team) continue;
      if (Math.hypot(ally.x - centroid.x, ally.y - centroid.y) > radius) continue;
      ally.hp = Math.min(ally.maxHp, ally.hp + heal);
      ally.buffMul = Math.max(ally.buffMul, buff);
    }
    return;
  }

  if (deploy.kind === "banner") {
    const buff = deploy.buffMul ?? 1.3;
    const poke = deploy.damage ?? 0;
    for (const ally of state.units) {
      if (ally.phase !== "alive" || ally.team !== team) continue;
      if (Math.hypot(ally.x - centroid.x, ally.y - centroid.y) > radius) continue;
      ally.buffMul = Math.max(ally.buffMul, buff);
    }
    if (poke > 0) {
      for (const enemy of state.units) {
        if (enemy.phase !== "alive" || enemy.team === team) continue;
        if (Math.hypot(enemy.x - centroid.x, enemy.y - centroid.y) > radius) continue;
        applyAoeHit(state, enemy, poke, team);
      }
    }
    return;
  }

  if (deploy.kind === "blight_seed") {
    const blightSec = deploy.blightSec ?? 3;
    const dmg = deploy.damage ?? 0;
    for (const enemy of state.units) {
      if (enemy.phase !== "alive" || enemy.team === team) continue;
      if (Math.hypot(enemy.x - centroid.x, enemy.y - centroid.y) > radius) continue;
      enemy.blight = Math.max(enemy.blight, blightSec);
      if (dmg > 0) applyAoeHit(state, enemy, dmg, team);
    }
    return;
  }

  if (deploy.kind === "judgment_slam" || deploy.kind === "blood_rite") {
    const dmg = deploy.damage ?? 20;
    const buff = deploy.buffMul ?? 1.4;
    const heal = deploy.heal ?? 0;
    for (const enemy of state.units) {
      if (enemy.phase !== "alive" || enemy.team === team) continue;
      if (Math.hypot(enemy.x - centroid.x, enemy.y - centroid.y) > radius) continue;
      applyAoeHit(state, enemy, dmg, team);
    }
    for (const unit of spawned) {
      unit.buffMul = Math.max(unit.buffMul, buff);
      if (heal > 0) unit.hp = Math.min(unit.maxHp, unit.hp + heal);
    }
  }
}

function packCentroid(units: readonly IwUnit[]): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const u of units) {
    x += u.x;
    y += u.y;
  }
  return { x: x / units.length, y: y / units.length };
}

function nearestEnemyInRadius(
  state: InfiniteWarState,
  from: IwUnit,
  radius: number,
): IwUnit | null {
  let best: IwUnit | null = null;
  let bestDist = radius;
  for (const unit of state.units) {
    if (unit.phase !== "alive" || unit.team === from.team) continue;
    const d = Math.hypot(unit.x - from.x, unit.y - from.y);
    if (d > bestDist) continue;
    bestDist = d;
    best = unit;
  }
  return best;
}

function applyDirectHit(
  state: InfiniteWarState,
  target: IwUnit,
  damage: number,
  owner: IwTeam,
): void {
  if (target.phase !== "alive") return;
  target.hp -= damage;
  if (target.hp <= 0) killUnit(state, target, owner);
}

function formationOffsets(
  formation: string,
  count: number,
): ReadonlyArray<{ dx: number; dy: number }> {
  const table = C.formations as Record<string, ReadonlyArray<{ dx: number; dy: number }>>;
  const listed = table[formation];
  if (listed && listed.length >= count) return listed.slice(0, count);
  if (count <= 1) return [{ dx: 0, dy: 0 }];
  const out: { dx: number; dy: number }[] = [];
  for (let i = 0; i < count; i += 1) {
    const t = count === 1 ? 0 : (i / (count - 1)) * 2 - 1;
    out.push({ dx: t * 28, dy: Math.abs(t) * 10 });
  }
  return out;
}
